import { useState, type CSSProperties, type DragEvent } from 'react';
import { uploadFile } from '../../../api/forms-api';
import { actionUrl } from '../../../api/http';
import { useUiLocale } from '../../../app/ui-locale';
import { useFeedback } from '../../../components/Feedback';
import type { FormField } from '../../../models/form';
import { isMultipleFileField, parseFileValues, serializeFileValues } from '../../../utils/file-values';
import { publicCopy, type PublicCopy } from '../public-copy';

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg']);
const VIDEO_EXTENSIONS = new Set(['mp4', 'webm', 'ogg', 'mov']);
const AUDIO_EXTENSIONS = new Set(['mp3', 'wav', 'ogg', 'm4a']);
const FILE_ENDPOINT_REVISION = '2.6.0.20-r2';

export type SubmissionFileAccess = 'owner' | 'viewer';

function storedFileName(value: string): string | undefined {
  // Legacy definitions can contain HTML-encoded query separators. Normalize the
  // reference before extracting the physical stored name so preview URLs are
  // always rebuilt in one canonical form.
  const clean = value.trim().replace(/&amp;/gi, '&');
  const staticMatch = /\/DesktopModules\/FaraDynamicForms\/UserMedia\/([^?#]+)/i.exec(clean);
  if (staticMatch?.[1]) {
    try { return decodeURIComponent(staticMatch[1]); } catch { return staticMatch[1]; }
  }
  const handlerMatch = /[?&]FileName=([^&#]+)/i.exec(clean);
  if (handlerMatch?.[1]) {
    try { return decodeURIComponent(handlerMatch[1].replace(/\+/g, ' ')); } catch { return handlerMatch[1]; }
  }
  if (/^(?:https?:)?\/\//i.test(clean) || clean.startsWith('/')) return undefined;
  return clean;
}

function extension(fileName: string): string {
  const clean = storedFileName(fileName) ?? ((fileName.split('?')[0] ?? fileName).split('#')[0] ?? fileName);
  const index = clean.lastIndexOf('.');
  return index >= 0 ? clean.slice(index + 1).toLowerCase() : '';
}

export function displayFileName(fileName: string): string {
  const clean = storedFileName(fileName) ?? fileName.trim();
  const match = /^\d{17}_[0-9a-f]{12}_(.+)$/i.exec(clean);
  return match?.[1] || clean;
}

function secureFileUrl(stored: string, secureSubmissionId: number, access: SubmissionFileAccess, download: boolean): string {
  if (access === 'viewer') {
    return actionUrl('FormsHandler', 'GetSubmittedFile', {
      SubmittedFormId: secureSubmissionId,
      FileName: stored,
      Download: download ? 1 : 0,
      dfv: FILE_ENDPOINT_REVISION
    });
  }
  return actionUrl('MySubmissionsHandler', 'GetFile', {
    SubmittedFormId: secureSubmissionId,
    FileName: stored,
    Download: download ? 1 : 0,
    dfv: FILE_ENDPOINT_REVISION
  });
}

export function fileUrl(fileName: string, secureSubmissionId = 0, submissionFileAccess: SubmissionFileAccess = 'owner'): string {
  const clean = fileName.trim().replace(/&amp;/gi, '&');
  const stored = storedFileName(clean);
  if (!stored) return clean;
  if (secureSubmissionId > 0) return secureFileUrl(stored, secureSubmissionId, submissionFileAccess, false);
  return actionUrl('FormsHandler', 'GetUploadedFile', { FileName: stored, dfv: FILE_ENDPOINT_REVISION });
}

export function fileDownloadUrl(fileName: string, secureSubmissionId = 0, submissionFileAccess: SubmissionFileAccess = 'owner'): string {
  const clean = fileName.trim().replace(/&amp;/gi, '&');
  const stored = storedFileName(clean);
  if (!stored) return clean;
  if (secureSubmissionId > 0) return secureFileUrl(stored, secureSubmissionId, submissionFileAccess, true);
  return actionUrl('FormsHandler', 'GetUploadedFile', { Download: 1, FileName: stored, dfv: FILE_ENDPOINT_REVISION });
}

function isAccepted(file: File, accept?: string): boolean {
  const rules = accept?.split(',').map((item) => item.trim().toLowerCase()).filter(Boolean) ?? [];
  if (!rules.length) return true;
  const name = file.name.toLowerCase();
  const mime = file.type.toLowerCase();
  return rules.some((rule) => rule.startsWith('.') ? name.endsWith(rule) : rule.endsWith('/*') ? mime.startsWith(rule.slice(0, -1)) : rule.includes('/') ? mime === rule : name.endsWith(`.${rule.replace(/^\./, '')}`));
}

function validateLocalFile(field: FormField, file: File, copy: PublicCopy): string | undefined {
  if (!isAccepted(file, field.validation?.accept)) return field.validation?.acceptMessage || copy.fileTypeInvalid;
  if (field.validation?.minFileSizeKb && file.size < field.validation.minFileSizeKb * 1024) return field.validation.minFileSizeMessage || copy.fileTooSmall;
  if (field.validation?.maxFileSizeKb && file.size > field.validation.maxFileSizeKb * 1024) return field.validation.maxFileSizeMessage || copy.fileTooLarge;
  return undefined;
}

export function FilePreviewItem({ fileName, compact = false, editable = false, secureSubmissionId = 0, submissionFileAccess = 'owner', onRemove, onMoveUp, onMoveDown }: { fileName: string; compact?: boolean; editable?: boolean; secureSubmissionId?: number; submissionFileAccess?: SubmissionFileAccess; onRemove?: () => void; onMoveUp?: () => void; onMoveDown?: () => void }) {
  const { locale } = useUiLocale();
  const copy = publicCopy(locale);
  const ext = extension(fileName);
  const url = fileUrl(fileName, secureSubmissionId, submissionFileAccess);
  const downloadUrl = fileDownloadUrl(fileName, secureSubmissionId, submissionFileAccess);
  const visibleName = displayFileName(fileName);
  const isImage = IMAGE_EXTENSIONS.has(ext);
  const isVideo = VIDEO_EXTENSIONS.has(ext);
  const isAudio = AUDIO_EXTENSIONS.has(ext);
  const [previewFailed, setPreviewFailed] = useState(false);
  return <div className={`df-file-preview-card ${compact ? 'df-file-preview-card--compact' : ''} ${isImage && !previewFailed ? 'df-file-preview-card--image' : 'df-file-preview-card--document'}`}>
    <div className="df-file-preview-visual">
      {isImage && !previewFailed ? <a href={url} target="_blank" rel="noreferrer" className="df-file-preview-thumb"><img src={url} alt={copy.filePreviewAlt} loading="lazy" onError={() => setPreviewFailed(true)} /></a>
        : isVideo ? <video className="df-file-preview-media" controls preload="metadata"><source src={url} /></video>
          : isAudio ? <audio className="df-file-preview-audio" controls preload="metadata"><source src={url} /></audio>
            : <a href={url} target="_blank" rel="noreferrer" className="df-file-preview-thumb df-file-preview-thumb--document" title={previewFailed ? copy.imagePreviewUnavailable : undefined}><span className="df-file-icon">{ext ? ext.toUpperCase() : 'FILE'}</span></a>}
    </div>
    <div className="df-file-preview-meta">
      <div className="df-file-preview-name" title={visibleName}>{visibleName}</div>
      <div className="df-file-preview-actions no-print">
        <a href={url} target="_blank" rel="noreferrer" className="btn btn-default btn-xs">{copy.view}</a>
        <a href={downloadUrl} className="btn btn-success btn-xs">{copy.download}</a>
        {editable && onMoveUp ? <button type="button" className="btn btn-default btn-xs" onClick={onMoveUp} aria-label={copy.moveFileUp}>↑</button> : null}
        {editable && onMoveDown ? <button type="button" className="btn btn-default btn-xs" onClick={onMoveDown} aria-label={copy.moveFileDown}>↓</button> : null}
        {editable && onRemove ? <button type="button" className="btn btn-danger btn-xs" onClick={onRemove}>{copy.delete}</button> : null}
      </div>
    </div>
  </div>;
}

interface Props {
  field: FormField;
  value: unknown;
  readOnly: boolean;
  disabled: boolean;
  controlId: string;
  className: string;
  style?: CSSProperties;
  attributes: Record<string, string | boolean | number>;
  describedBy?: string;
  compact?: boolean;
  secureSubmissionId?: number;
  submissionFileAccess?: SubmissionFileAccess;
  onChange: (value: unknown) => void;
}

export function FileUploadControl({ field, value, readOnly, disabled, controlId, className, style, attributes, describedBy, compact = false, secureSubmissionId = 0, submissionFileAccess = 'owner', onChange }: Props) {
  const { notify } = useFeedback();
  const { locale } = useUiLocale();
  const copy = publicCopy(locale);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState('');
  const multiple = isMultipleFileField(field);
  const files = parseFileValues(value, multiple);
  const invalid = /(?:^|\s)is-invalid(?:\s|$)/.test(className);
  const maxFiles = Math.max(1, Math.min(50, field.validation?.maximumFiles ?? (multiple ? 10 : 1)));

  const commit = (next: string[]) => onChange(serializeFileValues(next, multiple));

  const upload = async (selected: File[]) => {
    if (!selected.length || disabled) return;
    const available = Math.max(0, maxFiles - files.length);
    const accepted = selected.slice(0, multiple ? available : 1);
    if (!accepted.length) { notify(field.validation?.maximumFilesMessage || copy.maximumFiles(maxFiles), 'warning'); return; }
    for (const file of accepted) {
      const error = validateLocalFile(field, file, copy);
      if (error) { notify(`${file.name}: ${error}`, 'warning'); return; }
    }
    setUploading(true);
    const uploaded: string[] = [];
    try {
      for (let index = 0; index < accepted.length; index += 1) {
        setProgress(copy.uploadProgress(index + 1, accepted.length));
        uploaded.push((await uploadFile(accepted[index]!)).replace(/^"|"$/g, ''));
      }
      commit(multiple ? [...files, ...uploaded] : uploaded);
      notify(uploaded.length > 1 ? copy.filesUploaded(uploaded.length) : copy.fileUploaded, 'success');
      if (selected.length > accepted.length) notify(copy.limitedFilesUploaded(accepted.length), 'warning');
    } catch (error) {
      notify(error instanceof Error ? error.message : copy.uploadFailed, 'error');
    } finally {
      setUploading(false);
      setProgress('');
    }
  };

  const drop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    void upload(Array.from(event.dataTransfer.files));
  };

  if (readOnly) return files.length ? <div className="df-file-preview-list">{files.map((fileName) => <FilePreviewItem fileName={fileName} compact={compact} secureSubmissionId={secureSubmissionId} submissionFileAccess={submissionFileAccess} key={fileName} />)}</div> : <span className="df-readonly-empty">—</span>;

  return <div className={`df-file-control ${multiple ? 'is-multiple' : ''}`}>
    <div className={`df-file-dropzone ${dragging ? 'is-dragging' : ''} ${disabled ? 'is-disabled' : ''} ${invalid ? 'is-invalid' : ''}`} aria-invalid={invalid || undefined} onDragEnter={(event) => { event.preventDefault(); if (!disabled) setDragging(true); }} onDragOver={(event) => event.preventDefault()} onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setDragging(false); }} onDrop={drop}>
      <input
        {...attributes}
        id={controlId}
        name={field.name}
        className={className}
        style={style}
        type="file"
        multiple={multiple}
        capture={field.fileCapture || undefined}
        disabled={disabled || uploading || (multiple && files.length >= maxFiles)}
        accept={field.validation?.accept}
        aria-describedby={describedBy}
        onChange={(event) => { const input = event.currentTarget; void upload(Array.from(input.files ?? [])).finally(() => { input.value = ''; }); }}
      />
      <div className="df-file-dropzone-copy"><strong>{multiple ? copy.chooseFiles : copy.chooseFile}</strong><small>{multiple ? copy.filesRegistered(files.length, maxFiles) : copy.oneFileAllowed}</small></div>
    </div>
    {uploading ? <div className="df-file-upload-progress" role="status"><span className="df-spinner" /> {progress || copy.uploading}</div> : null}
    {files.length ? <div className="df-file-preview-list">{files.map((fileName, index) => <FilePreviewItem
      key={`${fileName}-${index}`}
      fileName={fileName}
      compact={compact}
      secureSubmissionId={secureSubmissionId}
      submissionFileAccess={submissionFileAccess}
      editable={!disabled && !uploading}
      onRemove={() => commit(files.filter((_, itemIndex) => itemIndex !== index))}
      onMoveUp={index > 0 ? () => { const next = [...files]; [next[index - 1], next[index]] = [next[index]!, next[index - 1]!]; commit(next); } : undefined}
      onMoveDown={index < files.length - 1 ? () => { const next = [...files]; [next[index], next[index + 1]] = [next[index + 1]!, next[index]!]; commit(next); } : undefined}
    />)}</div> : <span className="text-muted">{copy.noFile}</span>}
  </div>;
}
